import React from "react";
import { Button } from "@/components/ui/button";
import ExerciseCard from "./ExerciseCard";
import { DailyWorkoutHeaderProps, ExerciseSet } from "@/lib/types";
import {
  CompleteWorkout,
  WorkoutExercise,
  Exercise,
  UserWeekSummary,
} from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { normalizeDate } from "@/lib/dateUtils";

interface DailyWorkoutProps {
  workout: CompleteWorkout;
  onWorkoutUpdated: () => void;
  weekSummary?: UserWeekSummary;
}

const DailyWorkoutHeader: React.FC<DailyWorkoutHeaderProps> = ({
  date,
  totalExercises,
  isJokerDay,
  jokerDayAvailable,
  onJokerDay,
  onCompleteAll,
  isPastDate,
}) => {
  return (
    <div className="px-4 pt-4">
      <div className="flex justify-between items-center">
        <h2 className="font-heading text-lg font-bold">
          {isPastDate ? "Past Workout" : "Today's Workout"}
        </h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className={`px-3 py-1 h-auto rounded-full text-sm font-semibold flex items-center
              ${
                isJokerDay
                  ? "bg-warning text-neutral border-warning"
                  : "text-warning border-warning"
              }`}
            onClick={onJokerDay}
            disabled={isPastDate || (!isJokerDay && !jokerDayAvailable)}
          >
            <span className="material-icons text-sm mr-1">
              {isJokerDay ? "remove_circle" : "casino"}
            </span>
            {isJokerDay ? "Remove Joker" : "Joker"}
          </Button>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        {isPastDate
          ? "Past workouts can't be modified"
          : isJokerDay
          ? "Joker day - Skip your workout guilt-free"
          : `Complete ${totalExercises} exercises • ~30 minutes`}
      </p>
    </div>
  );
};

const DailyWorkout: React.FC<DailyWorkoutProps> = ({
  workout,
  onWorkoutUpdated,
  weekSummary,
}) => {
  const { toast } = useToast();

  // Check if the workout date is in the past
  const isPastDate = React.useMemo(() => {
    const workoutDate = new Date(workout.date);
    workoutDate.setHours(0, 0, 0, 0); // Midnight

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Midnight

    return workoutDate < today;
  }, [workout.date]);

  const handleExerciseComplete = async (
    exerciseId: number,
    completed: boolean,
    exerciseSets: ExerciseSet[]
  ) => {
    try {
      // Don't allow modifying past workouts
      if (isPastDate) {
        toast({
          title: "Past Workout",
          description: "You cannot modify exercises for past dates",
          variant: "destructive",
        });
        return;
      }

      // Use the actual values provided by the user
      const actualReps = exerciseSets.map((set) => set.actualReps || 0);
      const actualDuration = exerciseSets[0]?.actualDuration; // Just take the first one for duration-based exercises

      // Store the rep counts as a JSON string in a query parameter
      const repsString = JSON.stringify(actualReps);

      // Save the highest rep count in the actualReps field, which is a single integer
      const maxReps = actualReps.length > 0 ? Math.max(...actualReps) : 0;

      console.log("Saving exercise data:", {
        exerciseId,
        completed,
        actualReps,
        repsString,
        maxReps,
        actualDuration,
      });

      // Using the new consolidated API endpoint
      await apiRequest(
        "POST",
        `/api/workouts?operation=complete&id=${workout.id}&exerciseId=${exerciseId}`,
        {
          completed,
          actualReps: actualReps, // Send the full array of rep counts
          actualDuration,
        }
      );

      // Store rep counts in localStorage with both workout and exercise ID for better tracking
      const storageKey = `workout_${workout.id}_exercise_${exerciseId}_reps`;
      localStorage.setItem(storageKey, repsString);
      console.log("Saved rep counts to localStorage:", storageKey, repsString);

      onWorkoutUpdated();
    } catch (error) {
      console.error("Failed to update exercise completion", error);

      // Handle past date error specifically
      if (error instanceof Error && error.message.includes("past date")) {
        toast({
          title: "Past Workout",
          description: "You cannot modify exercises for past dates",
          variant: "destructive",
        });
      }
    }
  };

  const handleJokerDay = async () => {
    try {
      // Don't allow modifying past workouts
      if (isPastDate) {
        toast({
          title: "Past Workout",
          description: "You cannot modify joker day status for past dates",
          variant: "destructive",
        });
        return;
      }

      // If already a joker day, remove joker day status
      const isRemovingJoker = workout.isJokerDay;

      // Using the consolidated API endpoint
      await apiRequest(
        "POST",
        `/api/users/${workout.userId}?operation=joker-day`,
        {
          date: workout.date,
          remove: isRemovingJoker, // Pass true to remove joker day, undefined/false to add it
        }
      );

      toast({
        title: isRemovingJoker ? "Joker Day Removed" : "Joker Day Used",
        description: isRemovingJoker
          ? "You've removed the joker day for today."
          : "You've successfully used a joker day for today.",
        variant: "default",
      });

      onWorkoutUpdated();
    } catch (error) {
      console.error("Failed to handle joker day", error);

      // Handle the specific error when maximum joker days are used
      if (
        error instanceof Error &&
        error.message.includes("Maximum joker days")
      ) {
        toast({
          title: "Maximum Joker Days Used",
          description:
            "You've already used all your available joker days for this week.",
          variant: "destructive",
        });
      } else if (
        error instanceof Error &&
        error.message.includes("past date")
      ) {
        toast({
          title: "Past Workout",
          description: "You cannot modify joker day status for past dates",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description:
            "Failed to handle joker day operation. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  // Rest day functionality has been removed

  const handleCompleteAll = () => {
    // To be implemented
    if (isPastDate) {
      toast({
        title: "Past Workout",
        description: "You cannot modify exercises for past dates",
        variant: "destructive",
      });
      return;
    }

    alert("This would mark all exercises as complete");
  };

  if (!workout) return <div className="px-4 pt-4">Loading workout...</div>;

  return (
    <>
      <DailyWorkoutHeader
        date={new Date(workout.date)}
        totalExercises={workout.totalExercises}
        isJokerDay={workout.isJokerDay}
        jokerDayAvailable={
          !workout.isJokerDay &&
          (!weekSummary ||
            weekSummary.jokerDaysTaken < weekSummary.maxJokerDays)
        }
        onJokerDay={handleJokerDay}
        onCompleteAll={handleCompleteAll}
        isPastDate={isPastDate}
      />

      <div className="px-4 space-y-4">
        {isPastDate && (
          <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 my-4 text-center">
            <p className="text-gray-500 text-sm">
              This is a past workout. You cannot edit exercises or mark this as
              a joker day.
            </p>
          </div>
        )}

        {workout.exercises.map((we) => (
          <ExerciseCard
            key={we.id}
            id={we.id}
            workoutId={workout.id}
            name={we.exercise.name}
            description={we.exercise.description || undefined}
            sets={we.sets || 0}
            reps={we.reps || undefined}
            duration={we.duration || undefined}
            type={we.exercise.exerciseType as any}
            completed={we.completed}
            onComplete={(completed, exerciseSets) =>
              handleExerciseComplete(we.id, completed, exerciseSets)
            }
            disabled={isPastDate}
          />
        ))}

        {/* Rest day functionality has been removed */}

        {workout.isJokerDay && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-center">
            <div className="text-5xl mb-4">🃏</div>
            <h3 className="font-heading font-semibold text-lg mb-2">
              Joker Day
            </h3>
            <p className="text-sm text-gray-600">
              Everyone needs a break sometimes! You've used a joker day, so you
              can skip your workout without feeling guilty.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default DailyWorkout;
